// Form validation utilities for clean, validated data

// Phone validation - must be exactly 10 digits and not obviously fake
export const validatePhone = (phone) => {
  if (!phone) return { isValid: false, error: '' };
  
  const digits = phone.replace(/\D/g, '');
  
  if (digits.length === 0) {
    return { isValid: false, error: '' };
  }
  
  if (digits.length < 10) {
    return { isValid: false, error: 'Phone number must be 10 digits' };
  }
  
  if (digits.length > 10) {
    return { isValid: false, error: 'Phone number must be 10 digits' };
  }
  
  // Check for obviously fake numbers
  const fakePatterns = ['0000000000', '1111111111', '2222222222', '3333333333', '4444444444',
                        '5555555555', '6666666666', '7777777777', '8888888888', '9999999999',
                        '1234567890', '0987654321', '1234512345'];
  
  if (fakePatterns.includes(digits)) {
    return { isValid: false, error: 'Please enter a valid phone number' };
  }
  
  // Area code can't start with 0 or 1
  if (digits[0] === '0' || digits[0] === '1') {
    return { isValid: false, error: 'Please enter a valid US phone number' };
  }
  
  return { isValid: true, error: '' };
};

// Email validation - stricter regex for real emails
export const validateEmail = (email) => {
  if (!email) return { isValid: false, error: '' };
  
  const trimmed = email.trim().toLowerCase();
  
  // More comprehensive email regex
  const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
  
  if (!emailRegex.test(trimmed)) {
    return { isValid: false, error: 'Please enter a valid email address' };
  }
  
  // Check for obviously fake emails
  const fakePatterns = ['test@test', 'asdf@', 'fake@fake', 'a@a.', 'no@no.no'];
  if (fakePatterns.some(p => trimmed.includes(p))) {
    return { isValid: false, error: 'Please enter a real email address' };
  }
  
  // Check domain has reasonable length
  const domain = trimmed.split('@')[1];
  if (domain && domain.length < 4) {
    return { isValid: false, error: 'Please enter a valid email address' };
  }
  
  return { isValid: true, error: '' };
};

// Name validation - letters, spaces, hyphens, apostrophes only
// Must be a real name (no numbers, no gibberish like "asdf")
export const validateName = (name, fieldName = 'Name') => {
  if (!name || name.trim().length === 0) {
    return { isValid: false, error: '' };
  }
  
  const trimmed = name.trim();
  
  // Must be at least 2 characters
  if (trimmed.length < 2) {
    return { isValid: false, error: `${fieldName} must be at least 2 characters` };
  }
  
  // Only letters, spaces, hyphens, apostrophes allowed
  const nameRegex = /^[a-zA-Z\s\-']+$/;
  if (!nameRegex.test(trimmed)) {
    return { isValid: false, error: `${fieldName} can only contain letters, spaces, hyphens, and apostrophes` };
  }
  
  // Check for gibberish patterns (4+ consecutive consonants or vowels that aren't real)
  const lowerName = trimmed.toLowerCase();
  const gibberishConsonants = /[bcdfghjklmnpqrstvwxyz]{4,}/;
  const gibberishVowels = /[aeiou]{4,}/;
  
  // Allow common patterns like "schm" in Schmitt, but flag obvious gibberish
  const obviousGibberish = /^(asdf|qwer|zxcv|test|xxxx|aaaa|bbbb)/i;
  
  if (obviousGibberish.test(lowerName)) {
    return { isValid: false, error: `Please enter a valid ${fieldName.toLowerCase()}` };
  }
  
  // Must contain at least one vowel (real names do)
  if (!/[aeiou]/i.test(trimmed)) {
    return { isValid: false, error: `Please enter a valid ${fieldName.toLowerCase()}` };
  }
  
  return { isValid: true, error: '' };
};

// Date of birth validation
export const validateDOB = (dob, minAge = 0, maxAge = 120) => {
  if (!dob) return { isValid: false, error: '' };
  
  const birthDate = new Date(dob);
  
  // Check if date is valid
  if (isNaN(birthDate.getTime())) {
    return { isValid: false, error: 'Please enter a valid date' };
  }
  
  const today = new Date();
  
  // Can't be in the future
  if (birthDate > today) {
    return { isValid: false, error: 'Date cannot be in the future' };
  }
  
  // Calculate age
  let age = today.getFullYear() - birthDate.getFullYear();
  const monthDiff = today.getMonth() - birthDate.getMonth();
  
  if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
    age--;
  }
  
  if (age < minAge) {
    return { isValid: false, error: `Must be at least ${minAge} years old`, age };
  }
  if (age > maxAge) {
    return { isValid: false, error: 'Please enter a valid date of birth', age };
  }
  
  return { isValid: true, error: '', age };
};

// Minor validation (must be under 18 but at least 3)
export const validateMinorDOB = (dob) => {
  if (!dob) return { isValid: false, error: '' };
  
  const birthDate = new Date(dob);
  if (isNaN(birthDate.getTime())) {
    return { isValid: false, error: 'Please enter a valid date' };
  }
  
  const today = new Date();
  if (birthDate > today) {
    return { isValid: false, error: 'Date cannot be in the future' };
  }
  
  let age = today.getFullYear() - birthDate.getFullYear();
  const monthDiff = today.getMonth() - birthDate.getMonth();
  if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
    age--;
  }
  
  if (age >= 18) {
    return { isValid: false, error: 'Minor must be under 18 years old', age };
  }
  if (age < 3) {
    return { isValid: false, error: 'Participant must be at least 3 years old', age };
  }
  
  return { isValid: true, error: '', age };
};

// Password validation
export const validatePassword = (password) => {
  const errors = [];
  if (password.length < 8) errors.push('at least 8 characters');
  if (!/[A-Z]/.test(password)) errors.push('one uppercase letter');
  if (!/[a-z]/.test(password)) errors.push('one lowercase letter');
  if (!/[0-9]/.test(password)) errors.push('one number');
  
  return {
    isValid: errors.length === 0,
    error: errors.length > 0 ? `Password must contain ${errors.join(', ')}` : ''
  };
};

// Zip code validation (5 digits or 5+4 format)
// Also validates it's not obviously fake (00000, 99999, etc.)
export const validateZipCode = (zip) => {
  if (!zip) return { isValid: false, error: '' };
  
  const cleaned = zip.replace(/\s/g, '');
  const zipRegex = /^\d{5}(-\d{4})?$/;
  
  if (!zipRegex.test(cleaned)) {
    return { isValid: false, error: 'ZIP code must be 5 digits (or 5+4 format)' };
  }
  
  // Check for obviously fake zip codes
  const fiveDigit = cleaned.substring(0, 5);
  const fakePatterns = ['00000', '11111', '22222', '33333', '44444', '55555', '66666', '77777', '88888', '99999', '12345', '54321'];
  
  if (fakePatterns.includes(fiveDigit)) {
    return { isValid: false, error: 'Please enter a valid ZIP code' };
  }
  
  // First digit can't be 0 for most US zip codes (except some areas)
  // But we'll allow it since some valid zips start with 0 (e.g., 01001 in MA)
  
  return { isValid: true, error: '' };
};

// State validation (2-letter abbreviation)
export const validateState = (state) => {
  const states = [
    'AL', 'AK', 'AZ', 'AR', 'CA', 'CO', 'CT', 'DE', 'FL', 'GA',
    'HI', 'ID', 'IL', 'IN', 'IA', 'KS', 'KY', 'LA', 'ME', 'MD',
    'MA', 'MI', 'MN', 'MS', 'MO', 'MT', 'NE', 'NV', 'NH', 'NJ',
    'NM', 'NY', 'NC', 'ND', 'OH', 'OK', 'OR', 'PA', 'RI', 'SC',
    'SD', 'TN', 'TX', 'UT', 'VT', 'VA', 'WA', 'WV', 'WI', 'WY', 'DC'
  ];
  const upperState = state.toUpperCase();
  return {
    isValid: states.includes(upperState),
    error: state && !states.includes(upperState) ? 'Please enter a valid 2-letter state code' : '',
    formatted: upperState
  };
};

// Required field validation
export const validateRequired = (value, fieldName) => {
  const isValid = value && value.toString().trim().length > 0;
  return {
    isValid,
    error: !isValid ? `${fieldName} is required` : ''
  };
};

// Initials validation (2-3 uppercase letters)
export const validateInitials = (initials) => {
  const initialsRegex = /^[A-Z]{2,3}$/;
  return {
    isValid: initialsRegex.test(initials.toUpperCase()),
    error: initials && !initialsRegex.test(initials.toUpperCase()) ? 'Please enter 2-3 letter initials' : ''
  };
};

// Weight validation for bungee (90-240 lbs)
export const validateBungeeWeight = (weight) => {
  const numWeight = parseFloat(weight);
  if (isNaN(numWeight)) {
    return { isValid: false, error: 'Please enter a valid weight' };
  }
  if (numWeight < 90) {
    return { isValid: false, error: 'Minimum weight for bungee is 90 lbs' };
  }
  if (numWeight > 240) {
    return { isValid: false, error: 'Maximum weight for bungee is 240 lbs' };
  }
  return { isValid: true, error: '' };
};

// Format functions
export const formatPhoneNumber = (value) => {
  const phoneNumber = value.replace(/\D/g, '');
  
  if (phoneNumber.length === 0) {
    return '';
  } else if (phoneNumber.length <= 3) {
    return `(${phoneNumber}`;
  } else if (phoneNumber.length <= 6) {
    return `(${phoneNumber.slice(0, 3)}) ${phoneNumber.slice(3)}`;
  } else {
    return `(${phoneNumber.slice(0, 3)}) ${phoneNumber.slice(3, 6)}-${phoneNumber.slice(6, 10)}`;
  }
};

export const formatZipCode = (value) => {
  const digits = value.replace(/\D/g, '');
  if (digits.length <= 5) {
    return digits;
  }
  return `${digits.slice(0, 5)}-${digits.slice(5, 9)}`;
};

export const formatState = (value) => {
  return value.replace(/[^a-zA-Z]/g, '').toUpperCase().slice(0, 2);
};

// Capitalize first letter of each word
export const formatName = (value) => {
  return value
    .toLowerCase()
    .split(' ')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
};

// Get clean phone digits only (for API submission)
export const getPhoneDigits = (formattedPhone) => {
  return formattedPhone.replace(/\D/g, '');
};

// City validation - real city names only
export const validateCity = (city) => {
  if (!city) return { isValid: false, error: '' };
  
  const trimmed = city.trim();
  
  if (trimmed.length < 2) {
    return { isValid: false, error: 'City name must be at least 2 characters' };
  }
  
  // Only letters, spaces, hyphens, periods (for St. Louis, etc.)
  const cityRegex = /^[a-zA-Z\s\-.']+$/;
  if (!cityRegex.test(trimmed)) {
    return { isValid: false, error: 'City name can only contain letters, spaces, and hyphens' };
  }
  
  // Check for gibberish
  const obviousGibberish = /^(asdf|qwer|zxcv|test|xxxx|aaaa)/i;
  if (obviousGibberish.test(trimmed)) {
    return { isValid: false, error: 'Please enter a valid city name' };
  }
  
  // Must contain at least one vowel
  if (!/[aeiou]/i.test(trimmed)) {
    return { isValid: false, error: 'Please enter a valid city name' };
  }
  
  return { isValid: true, error: '' };
};

// Street address validation - basic sanity checks
export const validateStreetAddress = (address) => {
  if (!address) return { isValid: false, error: '' };
  
  const trimmed = address.trim();
  
  if (trimmed.length < 5) {
    return { isValid: false, error: 'Please enter a complete street address' };
  }
  
  // Should contain at least one number (street addresses typically do)
  if (!/\d/.test(trimmed)) {
    return { isValid: false, error: 'Street address should include a street number' };
  }
  
  // Should contain at least one letter
  if (!/[a-zA-Z]/.test(trimmed)) {
    return { isValid: false, error: 'Please enter a valid street address' };
  }
  
  // Check for gibberish
  const obviousGibberish = /^(123 asdf|test test|asdf|xxxx)/i;
  if (obviousGibberish.test(trimmed)) {
    return { isValid: false, error: 'Please enter a valid street address' };
  }
  
  return { isValid: true, error: '' };
};

// Comprehensive form validation
export const validateForm = (fields, rules) => {
  const errors = {};
  let isValid = true;
  
  for (const [fieldName, value] of Object.entries(fields)) {
    if (rules[fieldName]) {
      const result = rules[fieldName](value);
      if (!result.isValid && result.error) {
        errors[fieldName] = result.error;
        isValid = false;
      }
    }
  }
  
  return { isValid, errors };
};
